import PropTypes from 'prop-types';
import { useState } from 'react';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  AUTH_KEY,
  formatError,
  getCookieValue,
  setAuthCookie,
  windowExists,
} from '@classity-edtech/ui-base';
import { GoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { Roles, FreeFormName, FormStatus } from '@classity-edtech/platform-utils';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { useGoogleSignInMutation } from '../../services/authentication';
import { webPaths } from '../../routes/paths';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { useGetMyProfileQuery, useLazyGetMyProfileQuery } from '../../services/users';
import { useLazyGetMyResponsesQuery } from '../../services/response';

export interface GoogleButtonProps extends MuiBoxProps {
  text?: 'signin' | 'signin_with' | 'signup_with' | 'continue_with';
  useOneTap?: boolean;
}

export const GoogleButton = ({
  className,
  text = 'signin',
  useOneTap = false,
}: GoogleButtonProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch: any = useAppDispatch();
  const [searchParams] = useSearchParams();
  const previousUrl = searchParams.get('previousUrl');
  const [getUser] = useLazyGetMyProfileQuery();
  const {
    data: user,
    isUninitialized: uninitialisedUser,
    refetch: refetchUser,
  } = useGetMyProfileQuery();
  const [googleSignin] = useGoogleSignInMutation();
  const googleClientId = (windowExists.__UI_CONFIG__ as any)?.googleClientId;
  const [page] = useState(1);
  const limit = 1;

  const [getMyResponses, { data: myResponses }] = useLazyGetMyResponsesQuery();

  const width =
    (windowExists as any)?.innerWidth >= 568 ? 568 : (windowExists as any)?.innerWidth - 40;

  const getRole = () => {
    switch (pathname) {
      case `/${webPaths.teachSignup}`:
      case `/${webPaths.teachSignin}`:
        return Roles.PROSPECT_TUTOR;
      default:
        return Roles.STUDENT;
    }
  };

  const handleAuthAction = async (role: string) => {
    if (previousUrl) return previousUrl;
    if (role === Roles.ADMIN) return `/${webPaths.applications}`;
    if (role === Roles.PROSPECT_TUTOR) {
      if (!myResponses?.length) {
        const response = await getMyResponses({
          formName: FreeFormName.ONBOARDING_TUTOR,
          page,
          limit,
        });

        if ('data' in response && response.data?.[0]?.status === FormStatus.SUBMITTED) {
          return `/${webPaths.tutorDashboard}`;
        }
      }

      if (myResponses?.[0]?.status === FormStatus.SUBMITTED) {
        return `/${webPaths.tutorDashboard}`;
      }

      return `/${webPaths.tutorOnboarding}`;
    }
    if (role === Roles.STUDENT) {
      const user = await getUser();
      const profile: Record<string, any> | undefined = user?.data?.profile;

      if (!profile?.ids) {
        return `/${webPaths.studentProfile}`;
      }

      return `/${webPaths.learnerDashboard}`;
    }
    if (role === Roles.TUTOR) return `/${webPaths.tutorDashboard}`;
  };

  const handleConfirm = async (payload: { token: string; clientId: string }) => {
    const existingClientId = getCookieValue(AUTH_KEY);

    if (existingClientId) {
      const user = await getUser();

      if (user?.data?.roleId) {
        const url = await handleAuthAction(user.data.roleId);
        return ((windowExists as any).location = url);
      }
    }

    const signinResultData = await googleSignin({
      ...payload,
      role: getRole(),
    });

    if ('error' in signinResultData) {
      const error = formatError(signinResultData.error.message)?.message || 'An error occurred!';
      return dispatch(updateFlashMessage({ message: error, severity: 'error', open: true }));
    }

    if ('data' in signinResultData) {
      setAuthCookie(signinResultData.data as string);

      const user = await getUser();

      handleAuthAction(user?.data?.roleId);
    }
  };

  const handleGoogleSignin = async (payload: { token: string; clientId: string }) => {
    const result = await handleConfirm({ ...payload });

    await refetchUser();

    const user = await getUser();

    const url = await handleAuthAction(user?.data?.roleId);

    return ((windowExists as any).location = url);
  };

  if (useOneTap) {
    useGoogleOneTapLogin({
      cancel_on_tap_outside: false,
      disabled: !!user || uninitialisedUser,
      onSuccess: credentialResponse => {
        handleGoogleSignin({
          token: credentialResponse.credential!,
          clientId: credentialResponse.clientId || googleClientId,
        });
      },
      onError: () => {
        dispatch(updateFlashMessage({ open: true, severity: 'error', message: 'Login failed!' }));
      },
    });
  }

  return (
    <>
      {!useOneTap && (
        <Box sx={{ mx: 'auto', maxWidth: 'fit-content' }}>
          <GoogleLogin
            size='large'
            text={text}
            width={`${width}`}
            onSuccess={credentialResponse => {
              handleGoogleSignin({
                token: credentialResponse.credential!,
                clientId: credentialResponse.clientId!,
              });
            }}
            onError={() => {
              dispatch(
                updateFlashMessage({
                  open: true,
                  severity: 'error',
                  message: 'Login failed!',
                }),
              );
            }}
          />
        </Box>
      )}
    </>
  );
};

GoogleButton.propTypes = {
  className: PropTypes.string,
  useOneTap: PropTypes.bool,
  text: PropTypes.string,
};

GoogleButton.defaultProps = {
  className: null,
  useOneTap: false,
  text: 'signin',
};

export default styled<any>(GoogleButton)(({ theme }) => ``);
